import React, { useState, useEffect } from 'react';
import { 
  MdPeople, MdWork, MdCheckCircle, MdTrendingUp
} from 'react-icons/md';
import { FiRefreshCw, FiTrendingUp, FiTrendingDown, FiUpload, FiPackage, FiDatabase, FiStar, FiActivity, FiArrowRight, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from '../components/Layout';
import { apiService } from '../services/api';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDashboardStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!stats) {
    return (
      <Layout>
        <div className="text-center text-gray-600 dark:text-gray-400">
          Failed to load dashboard statistics
        </div>
      </Layout>
    );
  }

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

  return (
    <Layout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-navy-800 dark:text-white">
              📊 Dashboard Overview
            </h2>
          </div>
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {/* Total Users */}
          <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-100">Total Users</p>
                <h3 className="mt-2 text-4xl font-bold text-white">
                  {stats.overview.total_users}
                </h3>
                <p className="mt-2 flex items-center gap-1 text-sm text-blue-100">
                  <FiTrendingUp className="h-4 w-4" />
                  +{stats.overview.new_users_week} this week
                </p>
              </div>
              <div className="rounded-full bg-white/20 p-4">
                <MdPeople className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          {/* Total Jobs */}
          <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-100">Total Jobs</p>
                <h3 className="mt-2 text-4xl font-bold text-white">
                  {stats.overview.total_jobs}
                </h3>
                <p className="mt-2 flex items-center gap-1 text-sm text-purple-100">
                  <FiTrendingUp className="h-4 w-4" />
                  +{stats.overview.new_jobs_week} this week
                </p>
              </div>
              <div className="rounded-full bg-white/20 p-4">
                <MdWork className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          {/* CVs Uploaded */}
          <div className="rounded-2xl bg-gradient-to-br from-green-500 to-green-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-100">CVs Uploaded</p>
                <h3 className="mt-2 text-4xl font-bold text-white">
                  {stats.overview.total_cvs}
                </h3>
                <p className="mt-2 flex items-center gap-1 text-sm text-green-100">
                  <FiTrendingUp className="h-4 w-4" />
                  +{stats.overview.new_cvs_week} this week
                </p>
              </div>
              <div className="rounded-full bg-white/20 p-4">
                <FiUpload className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          {/* Total Analyzed CVs */}
          <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-100">CVs Analyzed</p>
                <h3 className="mt-2 text-4xl font-bold text-white">
                  {stats.cv_analysis?.analyzed || 0}
                </h3>
                <p className="mt-2 flex items-center gap-1 text-sm text-orange-100">
                  <MdCheckCircle className="h-4 w-4" />
                  Completed
                </p>
              </div>
              <div className="rounded-full bg-white/20 p-4">
                <MdCheckCircle className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Users Per Package */}
          <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-2">
              <FiUsers className="h-6 w-6 text-brand-500" />
              <h3 className="text-xl font-bold text-navy-800 dark:text-white">
                Users Per Package
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart 
                data={stats.package_distribution} 
                margin={{ top: 35, right: 30, left: 20, bottom: 50 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="title" 
                  stroke="#9ca3af" 
                  tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 500 }}
                  angle={0}
                  textAnchor="middle"
                  height={50}
                  interval={0}
                />
                <YAxis 
                  stroke="#9ca3af"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  allowDecimals={false}
                  width={40}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }} 
                  labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}
                  formatter={(value: any) => [value, 'Users']}
                  cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }}
                />
                <Bar 
                  dataKey="user_count" 
                  name="Users"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={100}
                  label={{ 
                    position: 'top', 
                    fill: '#1f2937',
                    fontSize: 14, 
                    fontWeight: 'bold',
                    offset: 10
                  }}
                >
                  {stats.package_distribution.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Analysis Trends Chart */}
          <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-2">
              <FiBarChart2 className="h-6 w-6 text-brand-500" />
              <h3 className="text-xl font-bold text-navy-800 dark:text-white">
                Analysis Trends (Last 7 Days)
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={stats.usage_per_day} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis 
                  dataKey="day" 
                  stroke="#9ca3af"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <YAxis 
                  stroke="#9ca3af"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '12px'
                  }}
                  labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Line 
                  type="monotone" 
                  dataKey="cv_analysis" 
                  stroke="#10b981" 
                  strokeWidth={3} 
                  name="CV Analysis"
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="interview_tips" 
                  stroke="#8b5cf6" 
                  strokeWidth={3} 
                  name="Interview Tips"
                  dot={{ fill: '#8b5cf6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* CV Analysis Stats */}
          <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-6 dark:border-purple-900/30 dark:bg-purple-900/10">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/30">
                <MdCheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-navy-800 dark:text-white">
                CV Analysis
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Analyzed:</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {stats.cv_analysis.analyzed}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Pending:</span>
                <span className="font-bold text-yellow-600 dark:text-yellow-400">
                  {stats.cv_analysis.pending}
                </span>
              </div>
            </div>
          </div>

          {/* Storage Stats */}
          <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-6 dark:border-blue-900/30 dark:bg-blue-900/10">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900/30">
                <FiDatabase className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-navy-800 dark:text-white">
                Storage Usage
              </h3>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                {stats.storage.total_mb} MB
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Total files stored
              </p>
            </div>
          </div>

          {/* Orders & Invoices Stats */}
          <div className="rounded-xl border-2 border-green-200 bg-green-50 p-6 dark:border-green-900/30 dark:bg-green-900/10">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
                <FiActivity className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-navy-800 dark:text-white">
                Orders & Invoices
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Orders:</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {stats.invoices?.completed || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Invoices:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">
                  {stats.invoices?.total || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* User Leaderboard */}
        <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-bold text-navy-800 dark:text-white">
              🏆 Top Contributors - Analyzed CVs
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total Analyzed CVs
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    #
                  </th>
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    USER
                  </th>
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    PACKAGE
                  </th>
                  <th className="pb-4 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    ANALYZED CVS
                  </th>
                  <th className="pb-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    DATE
                  </th>
                </tr>
              </thead>
              <tbody className="space-y-4">
                {stats.user_leaderboard.map((user: any, index: number) => (
                  <tr key={user.user_id} className="group">
                    <td className="py-4 pr-4">
                      <span className={`text-lg font-bold ${
                        index === 0 ? 'text-blue-600' : 
                        index === 1 ? 'text-blue-500' : 
                        index === 2 ? 'text-blue-400' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        #{index + 1}
                      </span>
                    </td>
                    <td className="py-4 pr-4">
                      <div>
                        <p className="font-semibold text-navy-800 dark:text-white">
                          {user.full_name}
                        </p>
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          {user.email}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 pr-4">
                      <span className="text-blue-600 hover:underline cursor-pointer dark:text-blue-400">
                        {user.package}
                      </span>
                    </td>
                    <td className="py-4 pr-4 text-center">
                      <span className="text-lg font-bold text-navy-800 dark:text-white">
                        {user.total_analyzed_cvs || user.total_activity || 0}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date().toLocaleDateString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="rounded-2xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <h3 className="mb-6 text-xl font-bold text-navy-800 dark:text-white">
            🕒 Recent Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    USER
                  </th>
                  <th className="pb-4 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    ACTION
                  </th>
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    TITLE
                  </th>
                  <th className="pb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    DATE/TIME
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_activity.map((activity: any, index: number) => {
                  // Determine the navigation path based on activity type
                  const getNavigationPath = () => {
                    switch (activity.type) {
                      case 'job':
                        return '/admin/jobs'; // Navigate to jobs list
                      case 'cv':
                      case 'bulk_upload':
                        // Extract job ID from activity data if available
                        const jobId = activity.job_id || activity.related_id || 1; // Fallback to 1 if no ID
                        return `/admin/job-details/${jobId}`; // Navigate to specific job details
                      case 'analysis':
                      case 'analysis_run':
                        const analysisJobId = activity.job_id || activity.related_id || 1;
                        return `/admin/job-details/${analysisJobId}`; // Navigate to job with analysis
                      default:
                        return '/admin/dashboard';
                    }
                  };
                  
                  const getActionType = () => {
                    if (activity.type === 'job') return 'Job Created';
                    if (activity.type === 'cv') return 'CV Uploaded';
                    if (activity.type === 'bulk_upload') return 'Bulk Upload';
                    if (activity.type === 'analysis' || activity.type === 'analysis_run') return 'Analysis Run';
                    return 'Activity';
                  };
                  
                  const getActionColor = () => {
                    if (activity.type === 'job') return 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400';
                    if (activity.type === 'cv') return 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400';
                    if (activity.type === 'bulk_upload') return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';
                    if (activity.type === 'analysis' || activity.type === 'analysis_run') return 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400';
                    return 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400';
                  };
                  
                  return (
                    <tr 
                      key={index} 
                      className="border-t border-gray-100 dark:border-navy-700 hover:bg-gray-50 dark:hover:bg-navy-700 cursor-pointer transition-colors"
                      onClick={() => window.location.href = getNavigationPath()}
                    >
                      <td className="py-4 pr-4">
                        <div>
                          <p className="font-semibold text-navy-800 dark:text-white">
                            {activity.user || 'System User'}
                          </p>
                          <p className="text-sm text-blue-600 dark:text-blue-400">
                            {activity.user_email || 'admin@system.com'}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 pr-4">
                        <div className="flex items-center justify-center">
                          <span className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium text-center ${getActionColor()}`}>
                            {getActionType()}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 pr-4">
                        <p className="font-medium text-navy-800 dark:text-white">
                          {activity.title}
                        </p>
                      </td>
                      <td className="py-4 pr-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(activity.timestamp).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric' 
                          })} {new Date(activity.timestamp).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
